import type * as hast from 'hast';
import { visit } from 'unist-util-visit';

import { isEmptyFootnoteLinkElement, isFootnoteBackrefElement, isLinkElement } from './utils';

/**
 * Markdown (or HTML from GitHUb) parsing generates empty <a> tags for footnote references, right after the word.
 * This plugin merge them with the adjacent word.
 */
export function mergeEmptyFootnotes() {
    return (tree) => {
        // unist-util-visit's Visitor type is wider than the narrowed node we handle here;
        // cast to a loose callable to bypass overload resolution without whack-a-mole suppressions.
        (visit as (...args: unknown[]) => void)(
            tree,
            (node) => isEmptyFootnoteLinkElement(node) || isFootnoteBackrefElement(node),
            (node: hast.Element, index: number, parent?: hast.Element | hast.Root) => {
                if (!parent) {
                    return;
                }

                if (isFootnoteBackrefElement(node)) {
                    // We remove the backref from GitHub, as it's an UI for the content.
                    parent.children.splice(index, 1);
                    return;
                }

                const previousNode = parent.children[index - 1];

                if (!previousNode) {
                    return;
                }

                if (isLinkElement(previousNode)) {
                    previousNode.properties = {
                        ...previousNode.properties,
                        dataFootnoteRef: true,
                    };

                    // Remove the link element, only the previous one will be used
                    parent.children.splice(index, 1);
                    return;
                }

                if (previousNode.type !== 'text') {
                    return;
                }

                // Footnote markers can follow CJK text, which JavaScript `\w` does not
                // classify as word characters.
                const matchWord = previousNode.value.match(/([\p{L}\p{N}_]+)$/u);
                if (!matchWord) {
                    // Remove the link element
                    parent.children.splice(index, 1);
                    return;
                }

                const word = matchWord[1];

                // Remove the word from the previous text node
                previousNode.value = previousNode.value.slice(
                    0,
                    previousNode.value.length - word.length
                );

                // Add the word to the link element
                node.children = [
                    {
                        type: 'text',
                        value: word,
                    },
                ];
            }
        );
    };
}
